<template>

    <div class="bloc-login-container">

        <div class="outlook-tip" v-if="provider === 'outlook'">

            <markdown class="outlook-text" :text="$t('global_microsoft_banner')" :blank="true" :linkBold="true"></markdown>

        </div>

        <div class="bloc-login">

            <h1 class="title-login">{{ $t('login_title') }}</h1>

            <span class="subtitle">{{ $t('login_email_secure') }}</span>

            <img class="lp-logo" :src="logoSrc" v-if="logoSrc">

            <input-text class="input-text"
                        ref="inputEmail"
                        :data="loginEmail"
                        :big="true"
                        :placeholder="$t('login_email_placeholder')"
                        :type="'email'"
                        :errorMessage="$t('login_error_invalid_email_password')"
                        :name="'email'"
                        :valid="isValidEmail"
                        :error="isErrorEmail"
                        @restart="isErrorEmail = false"
                        @blur="emailChange();testEmail()"
                        @pressEnter="$refs.inputPassword.focus()"
            ></input-text>

            <input-text class="input-text"
                        ref="inputPassword"
                        :big="true"
                        :placeholder="computedPasswordPlaceholder()"
                        :type="'password'"
                        :error="isErrorPassword"
                        :name="'password'"
                        @restart="isErrorPassword = false"
                        v-if="!isOnlyEmail"
                        @pressEnter="next"
            ></input-text>

            <div v-show="error" class="error-container">
                <span class="error-text">{{ computedMessageError }}</span>
            </div>

            <button
                    class="btn btn-blue full btn-next btn-big"
                    @click="next"
            >{{ $t('global_cta_next') }}
            </button>

            <p v-if="appPass" class="create-app-pass link" @click="createAppPass">{{ $t('login_create_app_password') }}</p>

            <img class="img-foxy-lock" src="/img/foxy/lock.png">

            <span class="span-secu">{{ $t('login_email_secure') }}</span>

        </div>

        <page-loader ref="loader"></page-loader>

        <modal-error-provider :email="emailError" :provider="provider" ref="MEP"></modal-error-provider>

        <modal-error-google ref="MEG" @goAppPass="goAppPass"></modal-error-google>

        <modal-outlook-invalid-credentials ref="MOIC"></modal-outlook-invalid-credentials>

    </div>

</template>

<script>

    import Vuex from 'vuex'

    import Api from '@/api'

    import InputText from "@/components/inputs/InputText"
    import Markdown from '@/components/Markdown'
    import ModalErrorProvider from '@/components/modal/ModalErrorProvider'
    import PageLoader from "@/components/PageLoader"
    import HeaderBand from '@/components/headers/HeaderBand'
    import ModalErrorGoogle from "@/components/modal/ModalErrorGoogle"
    import ModalOutlookInvalidCredentials from "@/components/modal/ModalOutlookInvalidCredentials"


    import Auth from '@/modules/auth'
    import Providers from '@/modules/providers'

    export default {

        name: 'login',

        components: {InputText, Markdown, ModalErrorProvider, PageLoader, HeaderBand, ModalErrorGoogle, ModalOutlookInvalidCredentials},

        computed: {

            ...Vuex.mapGetters(['page', 'loginEmail']),

            computedMessageError: function () {
                if (this.errorMessage === 'timeout') {

                    return this.$t('fetch_error_text')

                } else {

                    return this.$te(this.errorMessage) ? this.$t(this.errorMessage) : this.$t('login_error_invalid_email_password')

                }
            }

        },

        props: {

            provider: {
                type: String,
                default: ''
            },

            isOnlyEmail: {
                type: Boolean,
                default: false
            },

            logoSrc: {
                type: String,
                default: ''
            },

            appPass: {
                type: Boolean,
                default: false
            }

        },

        data() {
            return {
                error: false,
                regexMail: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                isValidEmail: false,
                isErrorEmail: false,
                isErrorPassword: false,
                errorMessage: '',
                emailError: ''
            }
        },

        mounted() {

            if (this.loginEmail) {
                const self = this
                // this vue is faster than input-text component for get value
                setTimeout(function () {
                    self.testEmail()
                }, 10)
                this.$refs.inputPassword.focus()
            } else {
                this.$refs.inputEmail.focus()
            }

        },

        methods: {

            ...Vuex.mapActions([
                'setEmail'
            ]),

            next() {

                if (this.isOnlyEmail) {

                    this.checkProviderBeforeContinue(this.$refs.inputEmail.value)

                } else {

                    if (this.testForm()) {

                        this.$refs.loader.start()

                        const email = this.$refs.inputEmail.value

                        this.setEmail(email)

                        Auth.connect(this, this.$refs.inputEmail.value, this.$refs.inputPassword.value)
                            .then(() => {

                                this.$router.push({path: '/fetch'})

                            })
                            .catch(error => {
                                this.$refs.loader.stop()
                                this.error = true

                                if (error.message) {

                                    if (this.provider === 'google' && (error.message === 'application_password' || error.message === 'invalid_credentials' || error.message === 'imap_disabled')) {

                                        this.$refs.MEG.open(error.message)

                                    } else if(this.provider === 'outlook' && error.message === 'invalid_credentials') {

                                        this.$refs.MOIC.open()

                                    } else if(error.message === 'mx_unsupported') {

                                        this.$refs.MEP.show(error)

                                    } else {

                                        this.error = true
                                        this.isErrorPassword = true

                                        if(error.message === 'mailbox_already_associated') {
                                            this.errorMessage = 'login_email_already_associated'
                                        } else {

                                            this.errorMessage = error.message
                                        }

                                    }

                                } else {
                                    this.errorMessage = ''
                                }
                            })

                    }
                }
            },

            computedPasswordPlaceholder () {
                const str = this.appPass ? 'login_application_password_placeholder' : 'login_password_placeholder'
                return this.$t(str)
            },

            testEmail() {

                const email = this.$refs.inputEmail.value

                if (email.length) {
                    this.isValidEmail = email.length > 0 && this.regexMail.test(email)
                    this.isErrorEmail = !this.regexMail.test(email)
                    return this.isValidEmail && !this.isErrorEmail
                }

            },

            createAppPass () {

                this.$emit('goAppPass')

            },

            testPassword() {

                this.isErrorPassword = !this.$refs.inputPassword.value.length
                return !this.isErrorPassword
            },

            testForm() {
                return this.testEmail() && this.testPassword()
            },

            emailChange() {

                const emailStr = this.$refs.inputEmail.value

                if (this.regexMail.test(emailStr)) {

                    this.setEmail(emailStr)

                    Api.provider.get(emailStr)
                        .then(data => {

                            if (Providers.isSpecificProvider(data.provider)) {

                                if(data.provider !== this.provider) {

                                    this.$router.push({path: `/login/${data.provider}`})

                                }

                            } else {

                                if(this.page !== 'login') {

                                    this.$router.push({path: `/login`})

                                }

                            }
                        })
                        .catch(error => {

                            if (error.message === 'mx_unsupported') {

                                this.$refs.MEP.show(error)
                            }
                        })
                }
            },

            goAppPass() {

                this.$emit('goAppPass')
            },

            checkProviderBeforeContinue(email) {

                Api.provider.get(email)
                    .then(data => {

                        if (Providers.isSpecificProvider(data.provider)) {

                            this.$router.push({ path: `/login/${ data.provider }` })

                        } else {

                            this.$router.push({ path: '/login' })

                        }

                    })
            }

        }
    }
</script>

<style scoped lang="scss">

    .bloc-login-container {
        width: 100%;
        @media screen and (min-width: $above-tablet-length) {
            flex-grow: 1;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            display: flex;
            padding: 50px;
        }
    }

    .outlook-tip {
        padding: 16px;
        background-color: #d7ecfa;
        border-radius: 8px;
        margin: 0 16px;
        max-width: 501px;
        @media screen and (min-width: $above-tablet-length) {
            margin-bottom: 20px;
        }
    }

    .outlook-text {
        font-size: 13px;
        line-height: 1.38;
        color: $aquamarine;
        text-align: left;
    }

    .bloc-login {
        display: flex;
        flex-direction: column;
        padding: 26px 16px 32px;
        align-items: center;
        @media screen and (min-width: $above-tablet-length) {
            max-width: 501px;
            background-color: $white;
            border-radius: 8px;
            box-shadow: 0 2px 10px 3px rgba(0, 0, 0, 0.15);
            padding: 50px 24px 44px;
            width: 501px;
        }
    }

    .lp-logo {
        max-width: 128px;
        max-height: 72px;
        object-fit: contain;
        margin: 24px 0;
        @media screen and (min-width: $above-tablet-length) {
            max-width: 162px;
        }
    }

    .title-login {
        font-size: 21px;
        font-weight: bold;
        @media screen and (min-width: $above-tablet-length) {
            margin-top: 0px;
            font-size: 36px;
            line-height: 1.44;
            color: $midnight;
        }
    }

    .subtitle {
        margin-top: 8px;
        font-size: 12px;
        font-weight: 500;
        color: rgba(59, 59, 84, 0.6);
        @media screen and (min-width: $above-tablet-length) {
            font-size: 20px;
            line-height: 1.5;
            color: $grey;
        }
    }

    .input-text {
        margin-top: 16px;
        width: 100%;
        @media screen and (min-width: $above-tablet-length) {
            margin-top: 24px;
        }
    }

    .btn-next {
        margin-top: 16px;
        @media screen and (min-width: $above-tablet-length) {
            margin-top: 24px;
            height: 56px;
        }
    }

    .create-app-pass {
        margin-top: 19px;
    }

    .img-foxy-lock {
        margin-top: 16px;
        height: 74px;
        object-fit: contain;
        @media screen and (min-width: $above-tablet-length) {
            margin-top: 38px;
        }
    }

    .span-secu {
        margin-top: 11px;
        font-size: 10px;
        font-weight: 500;
        color: $secur-text;
        @media screen and (min-width: $above-tablet-length) {
            margin-top: 16px;
        }
    }

    .error-container {
        margin-top: 4px;
        min-height: 16px;
        text-align: left;
    }

    .error-text {
        font-size: 13px;
        color: $raspberry;
    }


</style>
